import { AsyncPipe, NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, Renderer2, Signal, computed, effect, inject, untracked, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { PENDO_PLATFORM_KEY } from '@unified-ui/global-licenses';
import { NavigationRoutes, NavigationTabsComponent } from '@unified-ui/shared-components';
import { ModuleIcons } from '@unified-ui/shared/asset-lib';
import { AuthService, UserType, authSelectAnalyticData, authSelectAuthUsername, authSelectOrganizationName } from '@unified-ui/shared/auth-lib';
import { BuilderManagerService, BuilderMode, FeatureFlagKey, FeatureFlagsService, PlatformSignalService } from '@unified-ui/shared/services';
import { ThemeService } from '@unified-ui/theme';

import { Subject, filter, take, takeUntil } from 'rxjs';

import { toSignal } from '@angular/core/rxjs-interop';
import { EntitlementLevelEnum, EntitlementsService, FeatureMappingEnum } from '@unified-ui/entitlements';
import { MenuItem, MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DrawerModule } from 'primeng/drawer';
import { PanelMenu, PanelMenuModule } from 'primeng/panelmenu';
import { PopoverModule } from 'primeng/popover';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../environments/environment';
import { LazyLoadingDebugComponent } from './utils/lazy-loading-debug.component';

@Component({
    selector: 'unified-ui-root',
    templateUrl: './platform.component.html',
    styleUrls: ['./platform.component.scss'],
    standalone: true,
    imports: [
        AsyncPipe,
        NgComponentOutlet,
        NgClass,
        RouterLink,
        FormsModule,
        AvatarModule,
        ButtonModule,
        ToolbarModule,
        DrawerModule,
        PanelMenuModule,
        ConfirmDialogModule,
        PopoverModule,
        ToggleSwitchModule,
        ToastModule,
        TooltipModule,
        NavigationTabsComponent,
        LazyLoadingDebugComponent,
        RouterModule
    ]
})
export class PlatformComponent implements OnInit, OnDestroy {
    private _router = inject(Router);
    private _authService = inject(AuthService);
    private _builderManagerService = inject(BuilderManagerService);
    private _themeService = inject(ThemeService);
    private _store = inject(Store);
    private _messageService = inject(MessageService);
    private _signalService = inject(PlatformSignalService);
    private _renderer2 = inject(Renderer2);
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _entitlementsService = inject(EntitlementsService);
    private _featureFlagsService = inject(FeatureFlagsService);

    private _isEntitlementsServiceReady = toSignal(this._entitlementsService.ready$);
    // eslint-disable-next-line unicorn/consistent-function-scoping
    public theme: Signal<string> = computed(() => {
        return this._themeService.theme();
    });
    public darkChecked = this._themeService.theme() === 'guideline-dark';
    public sidebarOpen = true;

    protected builderComponentToRender = computed(() => {
        return this._builderManagerService.state?.open ? this._builderManagerService.state.builderComponent : undefined;
    });
    protected userName = this._store.select(authSelectAuthUsername);

    /** destroy reference */
    private readonly destroy$ = new Subject<void>();

    private readonly logo = viewChild<ElementRef>('logo');
    private readonly user = viewChild<ElementRef>('user');

    /**
     * Build menu items considering user entitlements
     * @param entitlements
     */
    applicationRouteItems = computed<MenuItem[]>(() => {
        const isSchedulerVisible = this._featureFlagsService.getFeatureFlagSignal<boolean>(FeatureFlagKey.IS_SCHEDULER_PAGE_VISIBLE);
        if (this._isEntitlementsServiceReady()) {
            return [
                { label: 'Home', icon: ModuleIcons.HOME, route: NavigationRoutes.home, routerLinkActiveOptions: { exact: true }, state: { sectionId: 'home' } },
                // ...(this._entitlementsService.hasEntitlement(FeatureMappingEnum.DATA_SOLUTIONS, EntitlementLevelEnum.FEATURE) ?
                //     [
                //         {
                //             label: 'Data Solutions',
                //             icon: ModuleIcons.DATASOLUTIONS,
                //             expanded: true,
                //             items: [
                //                 ...(this._entitlementsService.hasEntitlement(FeatureMappingEnum.DATA_SOLUTIONS, EntitlementLevelEnum.REPORT) ?
                //                     [
                //                         {
                //                             route: [NavigationRoutes.mediascope],
                //                             label: 'Reports',
                //                             styleClass: 'align-right',
                //                             icon: 'pi pi-plus-circle',
                //                             routerLinkActiveOptions: { exact: true },
                //                             state: { iconTooltipText: 'Create or Run a Report' },
                //                             command: (event: { originalEvent: Event }) => {
                //                                 const elementTarget = event.originalEvent?.target as HTMLElement;
                //                                 if (elementTarget.classList.contains('p-menuitem-icon')) {
                //                                     // Navigate to the mediascope route first
                //                                     this._router.navigate([NavigationRoutes.mediascope]);

                //                                     // Import the ReportChooserComponent
                //                                     import('@mediascope/report-chooser').then(reportChooserModule => {
                //                                         // Set the state in builderManagerService
                //                                         this._builderManagerService.state = { open: true, builderComponent: reportChooserModule.ReportChooserComponent, mode: BuilderMode.NEW, templateStep: BuilderTemplate.CHOOSER, firstOpening: true };
                //                                     });
                //                                 }
                //                             }
                //                         }
                //                     ]
                //                 :   [])
                //             ]
                //         }
                //     ]
                // :   []),
                ...(this._entitlementsService.hasEntitlement(FeatureMappingEnum.PLANNING, EntitlementLevelEnum.FEATURE) ?
                    [
                        {
                            label: 'Guideline Planning',
                            icon: ModuleIcons.PLANNING,
                            expanded: true,
                            // Use a custom property to track active state instead of route
                            state: { sectionId: 'guideline-planning' },
                            items: [
                                ...(this._entitlementsService.hasEntitlement(FeatureMappingEnum.PLANNING, EntitlementLevelEnum.REPORT) ?
                                    [
                                        {
                                            route: NavigationRoutes.mtReporter,
                                            label: 'Reports',
                                            styleClass: 'align-right',
                                            icon: 'pi pi-plus-circle',
                                            routerLinkActiveOptions: { exact: true },
                                            state: { iconTooltipText: 'Create New Report' },
                                            command: (event: { originalEvent: Event }) => {
                                                const elementTarget = event.originalEvent?.target as HTMLElement;
                                                if (elementTarget.classList.contains('p-menuitem-icon')) {
                                                    // Import the BuilderComponent from planning
                                                    import('@planning/feature/mt-reporter-lib').then(mtReporterLib => {
                                                        // Set the state in builderManagerService
                                                        this._builderManagerService.state = { open: true, builderComponent: mtReporterLib.BuilderComponent, mode: BuilderMode.NEW };
                                                    });
                                                }
                                            }
                                        }
                                    ]
                                :   []),
                                // FIXME: entitlement for Plan Approval Report pre-built report
                                // ...(this._entitlementsService.hasEntitlement(FeatureMappingEnum.PLANNING, EntitlementLevelEnum.REPORT) ?
                                //     [
                                //         {
                                //             route: [NavigationRoutes.mtPrebuilt],
                                //             routerLinkActiveOptions: { exact: true },
                                //             label: 'Plan Approval Report',
                                //             command: (event: { originalEvent: Event }) => {
                                //                 const elementTarget = event.originalEvent?.target as HTMLElement;
                                //                 // if (elementTarget.classList.contains('p-menuitem-icon')) {
                                //                 // Import the BuilderComponent from planning
                                //                 import('@planning/feature/pre-built-report-lib').then(mtPreBuiltReportLib => {
                                //                     // Set the state in builderManagerService
                                //                     this._builderManagerService.state = { open: true, builderComponent: mtPreBuiltReportLib.PreBuiltReportBuilderComponent, mode: BuilderMode.NEW };
                                //                 });
                                //                 // }
                                //                 // import('@planning/feature/pre-built-report-lib').then(module => {
                                //                 //     // First, store the builder component type
                                //                 //     const preBuiltReportBuilderComponent = module.PreBuiltReportBuilderComponent;

                                //                 //     // Update the builder manager service directly
                                //                 //     this._builderManagerService.state = { builderComponent: preBuiltReportBuilderComponent, open: true };

                                //                 //     // Use the injected ViewBuilderService instead of creating a new instance
                                //                 //     this._builderManagerService.state.builderComponent = preBuiltReportBuilderComponent;
                                //                 //     this._builderManagerService.state = { open: true, mode: BuilderMode.NEW };
                                //                 // });
                                //             }
                                //         }
                                //     ]
                                // :   []),
                                ...(this._entitlementsService.hasEntitlement(FeatureMappingEnum.PLANNING, EntitlementLevelEnum.SCHEDULER) && isSchedulerVisible() ?
                                    [
                                        {
                                            route: NavigationRoutes.scheduling,
                                            label: 'Scheduling Reports',
                                            styleClass: 'align-right',
                                            icon: 'pi pi-plus-circle',
                                            routerLinkActiveOptions: { exact: true },
                                            state: { iconTooltipText: 'Create New Schedule' },
                                            command: (event: { originalEvent: Event }) => {
                                                const elementTarget = event.originalEvent?.target as HTMLElement;
                                                if (elementTarget.classList.contains('p-menuitem-icon')) {
                                                    // Import the BuilderComponent from planning
                                                    import('@planning/feature/mt-scheduling-lib').then(schedulingLib => {
                                                        // Set the state in builderManagerService
                                                        this._builderManagerService.state = { open: true, builderComponent: schedulingLib.ScheduleBuilderComponent, mode: BuilderMode.NEW };
                                                    });
                                                }
                                            }
                                        }
                                    ]
                                :   []),
                                ...(this._entitlementsService.hasEntitlement(FeatureMappingEnum.PLANNING, EntitlementLevelEnum.FEATURE) ?
                                    [
                                        {
                                            label: 'Planning',
                                            styleClass: 'align-right',
                                            state: { itemToolTipText: 'Launch Guideline Planning' },
                                            command: () => {
                                                this._store
                                                    .select(authSelectOrganizationName)
                                                    .pipe(take(1))
                                                    .subscribe(orgName => {
                                                        window.open(`${environment.GLP_REDIRECTION_URL}/${orgName}`, '_blank');
                                                    });
                                            }
                                        }
                                    ]
                                :   [])
                            ]
                        }
                    ]
                :   []),
                {
                    label: 'Tools',
                    icon: ModuleIcons.TOOLS,
                    expanded: true,
                    visible: true,
                    // Use a custom property to track active state instead of route
                    state: { sectionId: 'tools' },
                    items: [
                        {
                            route: NavigationRoutes.folderManagement,
                            label: 'Folder Management',
                            styleClass: 'align-right',
                            routerLinkActiveOptions: { exact: true }
                        }
                    ]
                }
            ];
        } else {
            return [];
        }
    });

    constructor() {
        effect(() => {
            const messageSignal = this._signalService.platformSignal;
            if (!messageSignal.detail) return;
            untracked(() => {
                const message = { key: 'platform-message', life: 10_000, ...messageSignal };
                this._messageService.add(message);
            });
        });

        // Take once to activate Pendo script
        this._store
            .select(authSelectAnalyticData)
            .pipe(take(1))
            .subscribe(data => {
                if (data && data.uiAnalytics) {
                    this.configPendoScript(data.emailHash, data.orgId, data.userType as UserType, PENDO_PLATFORM_KEY);
                }
            });

        /** Subscribe on routes to redefine the way of Panel Menu to recognize an active route */
        this._router.events
            .pipe(
                takeUntil(this.destroy$),
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe(() => {
                /** gets the current route */
                const activeRoute = this._router.url;
                /** pass it ot the isItemActive function to compare, activating child and parent */
                PanelMenu.prototype.isItemActive = function (item: MenuItem): boolean {
                    /** If it is has a route, compares and updates */
                    if (item['route'] && !(item.items && item.items?.length > 0)) {
                        const routerLink = Array.isArray(item['route']) ? item['route'] : [item['route']];
                        const isActive = JSON.stringify(routerLink) === JSON.stringify([activeRoute]);

                        return isActive;
                    }
                    /** If has children, verifies if one of the children is active to activate parent */
                    if (item.items && item.items?.length > 0) {
                        let isChildActive = false;
                        for (const child of item.items) {
                            const routerLink = Array.isArray(child['route']) ? child['route'] : [child['route']];
                            isChildActive = JSON.stringify(routerLink) === JSON.stringify([activeRoute]);
                        }
                        return isChildActive;
                    }
                    return false;
                };
            });

        effect(() => {
            this.logo()!.nativeElement.classList.add('loaded');
            this.user()!.nativeElement.classList.add('loaded');
        });
    }

    public ngOnInit(): void {
        // Subscribe to router events to update menu item selection
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                // Force change detection to update menu item selection
                this._changeDetectorRef.detectChanges();
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Determines if a menu section is active based on the current route
     * @param item The menu item to check
     * @returns True if the section is active
     */
    public isSectionActive(item: MenuItem): boolean {
        if (!item.state || !item.state['sectionId']) return false;

        const currentUrl = this._router.url;

        // Don't highlight any section when on the home page
        if (currentUrl === '/' || currentUrl.includes('/home')) {
            return item.state['sectionId'] === 'home';
        }

        // Check for specific sections
        switch (item.state['sectionId']) {
            case 'guideline-planning':
                // Check if we're in the Guideline Planning section
                // This includes routes like /planning/mt-reporter and /planning/scheduling
                return currentUrl.includes('/planning') || currentUrl.includes('mt-reporter') || currentUrl.includes('scheduling');
            default:
                return false;
        }
    }

    public logout(): void {
        this._authService.logOut();
    }

    public onRouterLinkActive($event: boolean) {
        this._changeDetectorRef.detectChanges();
    }

    public onThemeChange() {
        // TODO: set up for more than 2 themes
        this._themeService.setTheme(this.darkChecked ? 'guideline-dark' : 'guideline-light');
    }

    /**
     * Toggles the expansion state of a menu item
     * @param item The menu item to toggle
     */
    public toggleMenuExpansion(event: any, item: MenuItem): void {
        if (event.target.classList?.contains('p-submenu-icon')) {
            item.expanded = !item.expanded;
            return;
        }

        // If the item has a route and is being expanded, navigate to its first child route
        if (item.items && item.items.length > 0 && item.items[0]['route']) {
            item.expanded = true;
            const firstChildRoute = item.items[0]['route'];
            this._router.navigate(Array.isArray(firstChildRoute) ? firstChildRoute : [firstChildRoute]);
        }
    }

    public toggleSidebar(): void {
        this.sidebarOpen = !this.sidebarOpen;
    }

    /**
     * Configures the pendo script with email hash and organization id and insert to the head component
     * @param emailHash hashed email in MD5
     * @param orgId organization id for grouping
     * @param userType Internal or External
     */
    private configPendoScript(emailHash: string, orgId: string, userType: UserType, applicationKey: string): void {
        const pendoScript = `(function(apiKey){
    (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
    v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
        y=e.createElement(n);y.async=!0;y.src='https://cdn.eu.pendo.io/agent/static/'+apiKey+'/pendo.js';
        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');pendo.initialize({
        visitor: {id: '${emailHash}'},account: {id: '${orgId}', userType: '${userType}'}});})('${applicationKey}');`;
        const script = this._renderer2.createElement('script');
        script.text = pendoScript;
        this._renderer2.appendChild(document.head, script);
    }
}
